import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Edit2, Check, Star, RefreshCw, Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FormDialog } from '@/components/ui/form-dialog';
import { useAuthStore } from '@/stores/auth.store';
import api from '@/lib/api';
import toast from 'react-hot-toast';
import type { Currency } from '@/types/pos';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { formatRate } from '@/lib/utils';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';

/** The readable sentence the server sends with a 400 (string or list of strings), if any. */
function serverMessage(err: any): string | undefined {
  const m = err?.response?.data?.message;
  return Array.isArray(m) ? m[0] : typeof m === 'string' ? m : undefined;
}

export function CurrenciesPage() {
  const { t } = useTranslation('settings');
  const { hasPermission } = useAuthStore();
  const canManage = hasPermission('settings:update');
  const queryClient = useQueryClient();
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [editRate, setEditRate] = useState('');
  const [disableTarget, setDisableTarget] = useState<Currency | null>(null);
  // POS-I355: Add Currency dialog.
  const emptyAdd = { code: '', name: '', symbol: '', rate: '' };
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(emptyAdd);
  const [addErrors, setAddErrors] = useState<Partial<Record<keyof typeof emptyAdd, string>>>({});

  const { data: currencies = [], isLoading } = useQuery<Currency[]>({
    queryKey: ['currencies'],
    queryFn: () => api.get('/currencies?all=true').then((r) => r.data),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      code,
      exchangeRate,
      isActive,
    }: {
      code: string;
      exchangeRate?: number;
      isActive?: boolean;
    }) => api.patch(`/currencies/${code}`, { exchangeRate, isActive }).then((r) => r.data),
    onSuccess: (updated: Currency, vars) => {
      if (vars.isActive === undefined) toast.success(t('curRateSaved', { code: updated.code, rate: formatRate(updated.exchangeRate) }));
      else toast.success(t(vars.isActive ? 'curEnabled' : 'curDisabled', { name: updated.name }));
      queryClient.invalidateQueries({ queryKey: ['currencies'] });
      queryClient.invalidateQueries({ queryKey: ['currencies-active'] });
      setEditingCode(null);
      setDisableTarget(null);
    },
    onError: (err: any) => { setDisableTarget(null); toast.error(serverMessage(err) ?? t('curUpdateFailed')); },
  });

  const setDefaultMutation = useMutation({
    mutationFn: (code: string) =>
      api.patch(`/currencies/${code}/set-default`).then((r) => r.data),
    onSuccess: () => {
      toast.success(t('curDefaultUpdated'));
      queryClient.invalidateQueries({ queryKey: ['currencies'] });
      queryClient.invalidateQueries({ queryKey: ['currencies-active'] });
    },
    onError: (err: any) => toast.error(serverMessage(err) ?? t('curDefaultFailed')),
  });

  const addMutation = useMutation({
    mutationFn: (body: { code: string; name: string; symbol: string; exchangeRate: number }) =>
      api.post('/currencies', body).then((r) => r.data),
    onSuccess: (created: Currency) => {
      toast.success(t('curAdded', { name: created.name }));
      queryClient.invalidateQueries({ queryKey: ['currencies'] });
      queryClient.invalidateQueries({ queryKey: ['currencies-active'] });
      setAddOpen(false);
      setAddForm(emptyAdd);
      setAddErrors({});
    },
    onError: (err: { response?: { status?: number } }) => {
      const msg = serverMessage(err) ?? t('curAddFailed');
      setAddErrors({ code: err?.response?.status === 409 ? t('curCodeExists', { code: addForm.code }) : msg });
    },
  });

  const submitAdd = () => {
    const code = addForm.code.trim().toUpperCase();
    const name = addForm.name.trim();
    const symbol = addForm.symbol.trim();
    const rate = Number(addForm.rate);
    const errs: typeof addErrors = {};
    if (!/^[A-Z]{3}$/.test(code)) errs.code = t('curCodeRange');
    else if (code === 'PKR' || currencies.some((c) => c.code === code)) errs.code = code === 'PKR' ? t('curCodePkr') : t('curCodeExists', { code });
    if (name.length < 1 || name.length > 60 || /[<>]/.test(name)) errs.name = t('curNameRange');
    if (symbol.length < 1 || symbol.length > 8 || /[<>]/.test(symbol)) errs.symbol = t('curSymbolRange');
    if (!addForm.rate.trim() || !Number.isFinite(rate) || rate <= 0 || rate > 999999) errs.rate = t('curRateRangeLong');
    setAddErrors(errs);
    if (Object.keys(errs).length) return;
    addMutation.mutate({ code, name, symbol, exchangeRate: rate });
  };

  const handleSaveRate = (code: string) => {
    const rate = parseFloat(editRate);
    if (isNaN(rate) || rate <= 0 || rate > 999999) {
      toast.error(t('curRateRange'));
      return;
    }
    updateMutation.mutate({ code, exchangeRate: rate });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">{t('currenciesTitle')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('curSubtitle')}
          </p>
        </div>
        {canManage && (
          <Button size="sm" onClick={() => { setAddForm(emptyAdd); setAddErrors({}); setAddOpen(true); }}>
            <Plus className="h-4 w-4 me-1" />
            {t('curAdd')}
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <Table mobileCards>
            <THead>
              <Tr hover={false}>
                <Th>{t('tblCurrency')}</Th>
                <Th>{t('tblCode')}</Th>
                <Th className="text-end">{t('tblRate')}</Th>
                <Th className="text-center">{t('tblStatus')}</Th>
                {canManage && <Th className="text-end">{t('tblActions')}</Th>}
              </Tr>
            </THead>
            <TBody>
              {currencies.map((currency) => (
                <Tr key={currency.code} className={!currency.isActive ? 'opacity-50' : ''}>
                  <Td className="td-name">
                    <div className="flex items-center gap-2">
                      <span className="text-base font-mono">{currency.symbol}</span>
                      <span>{currency.name}</span>
                      {currency.isDefault && (
                        <span className="inline-flex items-center gap-1 text-xs bg-warning/10 text-warning-text rounded-full px-2 py-0.5">
                          <Star className="h-3 w-3" /> {t('curDefault')}
                        </span>
                      )}
                    </div>
                  </Td>
                  <Td label={t('tblCode')} className="font-mono font-semibold">{currency.code}</Td>
                  <Td label={t('tblRate')} className="text-end">
                    {editingCode === currency.code ? (
                      <div className="flex items-center justify-end gap-2">
                        <Input
                          type="number"
                          step="any"
                          min="0.000001"
                          max="999999"
                          value={editRate}
                          onChange={(e) => setEditRate(e.target.value)}
                          className="w-28 h-7 text-end text-sm"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveRate(currency.code);
                            if (e.key === 'Escape') setEditingCode(null);
                          }}
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2"
                          onClick={() => handleSaveRate(currency.code)}
                          disabled={updateMutation.isPending}
                        >
                          <Check className="h-3.5 w-3.5 text-success-text" />
                        </Button>
                      </div>
                    ) : (
                      <span className="font-medium">
                        {currency.code === 'PKR'
                          ? '1.000000'
                          : formatRate(currency.exchangeRate)}
                      </span>
                    )}
                  </Td>
                  <Td label={t('tblStatus')} className="text-center">
                    <span
                      className={`text-xs rounded-full px-2 py-0.5 ${
                        currency.isActive
                          ? 'bg-success/10 text-success-text'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {currency.isActive ? t('curActive') : t('curInactive')}
                    </span>
                  </Td>
                  {canManage && (
                    <Td className="td-actions">
                      <div className="flex items-center justify-end gap-1">
                        {!currency.isDefault && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-xs"
                            onClick={() => setDefaultMutation.mutate(currency.code)}
                            disabled={setDefaultMutation.isPending}
                          >
                            {t('curSetDefault')}
                          </Button>
                        )}
                        {!currency.isDefault && currency.code !== 'PKR' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-xs"
                            onClick={() =>
                              currency.isActive
                                ? setDisableTarget(currency)
                                : updateMutation.mutate({ code: currency.code, isActive: true })
                            }
                            disabled={updateMutation.isPending}
                          >
                            {currency.isActive ? t('curDisable') : t('curEnable')}
                          </Button>
                        )}
                        {currency.code !== 'PKR' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2"
                            onClick={() => {
                              setEditingCode(currency.code);
                              setEditRate(String(Number(currency.exchangeRate)));
                            }}
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </Td>
                  )}
                </Tr>
              ))}
            </TBody>
          </Table>
        </div>
      )}

      <FormDialog
        open={addOpen}
        title={t('curAddTitle')}
        subtitle={t('curAddSubtitle')}
        width="28rem"
        submitLabel={t('curAdd')}
        isPending={addMutation.isPending}
        noValidate
        onSubmit={(e) => { e.preventDefault(); submitAdd(); }}
        onClose={() => setAddOpen(false)}
      >
        <div className="space-y-3">
          <div>
            <Label htmlFor="cur-code">{t('curFieldCode')}</Label>
            <Input id="cur-code" dir="ltr" maxLength={3} value={addForm.code} autoFocus
              onChange={(e) => { setAddForm({ ...addForm, code: e.target.value.toUpperCase() }); setAddErrors((p) => ({ ...p, code: undefined })); }} />
            {addErrors.code && <p className="mt-1 text-xs text-destructive">{addErrors.code}</p>}
          </div>
          <div>
            <Label htmlFor="cur-name">{t('curFieldName')}</Label>
            <Input id="cur-name" maxLength={60} value={addForm.name} onChange={(e) => { setAddForm({ ...addForm, name: e.target.value }); setAddErrors((p) => ({ ...p, name: undefined })); }} />
            {addErrors.name && <p className="mt-1 text-xs text-destructive">{addErrors.name}</p>}
          </div>
          <div>
            <Label htmlFor="cur-symbol">{t('curFieldSymbol')}</Label>
            <Input id="cur-symbol" maxLength={8} value={addForm.symbol} onChange={(e) => { setAddForm({ ...addForm, symbol: e.target.value }); setAddErrors((p) => ({ ...p, symbol: undefined })); }} />
            {addErrors.symbol && <p className="mt-1 text-xs text-destructive">{addErrors.symbol}</p>}
          </div>
          <div>
            <Label htmlFor="cur-rate">{t('curFieldRate')}</Label>
            <Input id="cur-rate" dir="ltr" type="number" step="any" min="0.000001" max="999999" value={addForm.rate}
              onChange={(e) => { setAddForm({ ...addForm, rate: e.target.value }); setAddErrors((p) => ({ ...p, rate: undefined })); }} />
            <p className="mt-1 text-xs text-muted-foreground">{t('curRateHelp')}</p>
            {addErrors.rate && <p className="mt-1 text-xs text-destructive">{addErrors.rate}</p>}
          </div>
        </div>
      </FormDialog>

      <ConfirmDialog
        open={disableTarget !== null}
        onCancel={() => setDisableTarget(null)}
        onConfirm={() => disableTarget && updateMutation.mutate({ code: disableTarget.code, isActive: false })}
        title={t('curDisableTitle', { name: disableTarget?.name ?? '' })}
        message={t('curDisableConfirm', { name: disableTarget?.name ?? '' })}
        confirmLabel={t('curDisable')}
        variant="warning"
        loading={updateMutation.isPending}
      />

      <p className="text-xs text-muted-foreground flex items-center gap-1">
        <RefreshCw className="inline h-3 w-3" />
        {t('curFooter')}
      </p>
    </div>
  );
}
